import NumberFlow from "@number-flow/react";
import { Slot, Slottable } from "@radix-ui/react-slot";
import { NavigateOptions, RegisteredRouter } from "@tanstack/react-router";
import { cn } from "@theprelude/core";
import { Button, GalaxySpinner, RefreshIcon } from "@theprelude/ds";
import { AnimatePresence, motion } from "framer-motion";
import { Children, ReactNode, Suspense } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { OverlaySchema } from "../overlays";

export const ColumnHeader = ({
  children,
  icon,
}: {
  children: ReactNode;
  icon?: ReactNode;
}) => (
  <div className="flex items-center justify-between">
    <div className="mr-2 font-serif text-[32px]">{children}</div>
    <span className="text-secondary-foreground">{icon}</span>
  </div>
);

export const CardHeader = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "flex items-center gap-2 font-serif text-[28px] leading-[1.2] font-medium",
      className,
    )}
  >
    {children}
  </div>
);

export const Card = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "bg-gradient-to-b from-surface-background to-surface-background/0 p-8 dark:from-secondary-background dark:to-secondary-background/0",
      className,
    )}
  >
    {children}
  </div>
);

export const ErrorBoundaryCard = ({ children }: { children: ReactNode }) => {
  return (
    <AnimatePresence>
      <ErrorBoundary FallbackComponent={FallbackCard}>
        <Suspense fallback={<LoadingCard />}>
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: "easeInOut" }}
          >
            {children}
          </motion.div>
        </Suspense>
      </ErrorBoundary>
    </AnimatePresence>
  );
};

const FallbackCard = ({
  resetErrorBoundary,
}: {
  resetErrorBoundary: () => void;
}) => {
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 1 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0, opacity: 0 }}
      transition={{ duration: 0.5, ease: "easeInOut" }}
    >
      <Card className="flex h-80 w-full flex-col items-center justify-center gap-2 text-center">
        <span className="text-sm">
          We had some trouble retrieving relevant data. Please try again in a
          few moments.
        </span>
        <Button
          icon={<RefreshIcon />}
          onClick={resetErrorBoundary}
          intent="pill"
        >
          Try again
        </Button>
      </Card>
    </motion.div>
  );
};

export const LoadingCard = () => (
  <motion.div
    initial={{ scale: 0.9, opacity: 1 }}
    animate={{ scale: 1, opacity: 1 }}
    exit={{ scale: 0, opacity: 0 }}
    transition={{ duration: 0.5, ease: "easeInOut" }}
  >
    <div className="flex items-center justify-center">
      <GalaxySpinner />
    </div>
  </motion.div>
);

export const Metric = ({
  label,
  count,
  className,
  labelClassName,
  countClassName,
}: {
  label: ReactNode;
  count: number;
  className?: string;
  labelClassName?: string;
  countClassName?: string;
}) => (
  <div className={cn("flex flex-col items-center", className)}>
    <div
      className={cn(
        "whitespace-nowrap text-base text-secondary-foreground",
        labelClassName,
      )}
    >
      {label}
    </div>
    <div
      className={cn(
        "text-[60px] leading-none font-medium",
        count > 0 ? "text-status-red" : "text-status-green",
        countClassName,
      )}
    >
      <NumberFlow value={count} />
    </div>
  </div>
);

export const MetricSet = ({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      "grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))]",
      className,
    )}
  >
    {Children.map(children, (child) => (
      <div className="border-r border-stroke-table last:border-0 dark:border-stroke-line">
        {child}
      </div>
    ))}
  </div>
);

export const ListItem = ({
  children,
  asChild,
  className,
}: {
  children: ReactNode;
  asChild?: boolean;
  className?: string;
}) => {
  const Comp = asChild ? Slot : "div";

  return (
    <Comp
      className={cn(
        "mb-2 grid w-full grid-cols-2 border-b border-stroke-table pb-2 dark:border-stroke-line",
        "last:mb-0 last:border-0 last:pb-0",
        className,
      )}
    >
      {children && <Slottable>{children}</Slottable>}
    </Comp>
  );
};

export const getNavigationOptions = ({
  overlay,
  hideExcepted,
}: {
  overlay: OverlaySchema;
  hideExcepted?: boolean;
}): NavigateOptions<RegisteredRouter> => ({
  to: window.location.pathname as RegisteredRouter["routeTree"]["path"],
  params: (prev) => prev,
  search: (prev) => ({ ...prev, overlay, hide_excepted: hideExcepted }),
});
